import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Briefcase, Building2, Lightbulb, Rocket, Sparkles, Target, TrendingUp, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/layout/PublicHeader";
import { useSEO } from "@/lib/useSEO";

// Founder-side landing — "Stop struggling to find investors". One-tap
// project submission for any logged-in user. Investor feed (separate page)
// surfaces these projects with gold/silver tiering based on category match.
export default function Founders() {
  useSEO({
    title: "Maqam Founders — Find your investor in 72 hours",
    description: "Submit your project, and Maqam routes it straight to investors who actually fund your category. Gold-shine matching, instant intro requests, no fees on the founder side.",
  });
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [form, setForm] = useState({
    title: "", one_liner: "", description: "",
    category: "it", stage: "idea", funding_usd: 100000,
    country: "Saudi Arabia", website: "",
    team_size: 1, has_revenue: false, monthly_users: 0,
    months_traction: 0, prior_exits: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [justScored, setJustScored] = useState(null); // shows score breakdown after submit

  useEffect(() => {
    api.get("/investors/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    if (user) {
      api.get("/founder/projects/mine").then((r) => setMyProjects(r.data || [])).catch(() => {});
    }
  }, [user]);

  const submit = async () => {
    if (!user) { navigate("/register?next=/founders"); return; }
    if (!form.title.trim() || !form.one_liner.trim()) {
      toast.error("Title and one-liner are required."); return;
    }
    if (form.one_liner.length > 160) {
      toast.error("One-liner must be 160 characters or less."); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/founder/projects", form);
      setMyProjects([r.data, ...myProjects]);
      setForm({ ...form, title: "", one_liner: "", description: "" });
      setShowForm(false);
      setJustScored(r.data); // surface the score breakdown
      toast.success("Your project is live in the investor feed.");
      // Smooth-scroll to the score card if it just appeared.
      setTimeout(() => {
        const el = document.getElementById("founder-score-card");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit project.");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteProject = async (id) => {
    try {
      await api.delete(`/founder/projects/${id}`);
      setMyProjects(myProjects.filter((p) => p.id !== id));
      toast.success("Project removed.");
    } catch { toast.error("Could not delete."); }
  };

  return (
    <div className="min-h-screen bg-[#1a1a1c] text-white" data-testid="founders-page">
      <PublicHeader />

      {/* HERO — silver radial like /hire */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse 100% 85% at 50% 45%, #e6e6eb 0%, #b8b8be 25%, #6e6e74 55%, #2a2a2e 90%)",
        }}
      >
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20 lg:pb-28">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/60 mb-6">
            / FOR FOUNDERS · KSA INVESTOR MARKETPLACE
          </div>
          <h1 className="font-display font-black text-5xl md:text-7xl lg:text-[5.2rem] tracking-tighter leading-[0.92] max-w-4xl">
            Stop struggling to find investors.
            <br />
            <span className="italic text-white/70">Let them find you.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/80 leading-relaxed">
            You have a project. A dream. A working MVP. But you don't know
            where to start, who to pitch, or how to get a meeting.{" "}
            <span className="text-white font-semibold">
              Through years of building Maqam, we've connected hundreds of
              KSA and global investors to our network.
            </span>{" "}
            Submit your project once — investors who fund your category see it
            <span className="text-[#FF9500] font-bold"> in gold</span>.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => {
                if (!user) navigate("/register?next=/founders");
                else setShowForm(true);
              }}
              className="inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-white/85 px-8 py-5 font-bold text-base transition-colors"
              data-testid="founders-cta-submit"
            >
              Submit your project <ArrowRight className="w-5 h-5" />
            </button>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center gap-3 border border-white/40 hover:border-white px-8 py-5 font-bold text-base transition-colors"
              data-testid="founders-cta-how"
            >
              How it works
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-t border-white/10 py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/55 mb-4">/ HOW IT WORKS</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-12">
            Four steps. Zero gatekeepers.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Lightbulb, n: "01", t: "Pitch in 160 chars", d: "Title + one-liner + category. Two minutes max." },
              { icon: Target,    n: "02", t: "Categorized",       d: "Pick the sector — IT, Health, FinTech, EdTech, Energy, etc." },
              { icon: Sparkles,  n: "03", t: "Gold-matched feed", d: "Investors in your category see your project glowing first." },
              { icon: Rocket,    n: "04", t: "Direct intros",     d: "When an investor signals interest, we connect you instantly." },
            ].map((s) => (
              <div key={s.n} className="bg-[#141416] border border-white/10 p-6 hover:border-[#FF9500]/50 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <s.icon className="w-6 h-6 text-[#FF9500]" />
                  <span className="font-mono text-xs text-white/40">{s.n}</span>
                </div>
                <div className="font-display font-bold text-xl mb-2">{s.t}</div>
                <p className="text-sm text-white/65">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUBMIT FORM (collapsible) */}
      {showForm && user && (
        <section className="border-t border-white/10 py-16 bg-[#0c0c0d]">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF9500] mb-3">/ NEW PROJECT</div>
            <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-8">Pitch your project</h2>
            <div className="space-y-5" data-testid="founders-form">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">Title*</label>
                <input
                  type="text" maxLength={80}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. NurAI — Arabic legal research"
                  className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                  data-testid="founders-form-title"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">One-liner* ({form.one_liner.length}/160)</label>
                <input
                  type="text" maxLength={160}
                  value={form.one_liner}
                  onChange={(e) => setForm({ ...form, one_liner: e.target.value })}
                  placeholder="AI-powered Arabic legal research assistant for KSA law firms."
                  className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                  data-testid="founders-form-oneliner"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                    data-testid="founders-form-category"
                  >
                    {categories.map((c) => (
                      <option key={c.key} value={c.key} className="bg-[#141416]">{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">Stage</label>
                  <select
                    value={form.stage}
                    onChange={(e) => setForm({ ...form, stage: e.target.value })}
                    className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                    data-testid="founders-form-stage"
                  >
                    <option value="idea"     className="bg-[#141416]">Idea</option>
                    <option value="mvp"      className="bg-[#141416]">MVP</option>
                    <option value="revenue"  className="bg-[#141416]">Revenue</option>
                    <option value="scaling"  className="bg-[#141416]">Scaling</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">Funding sought (USD)</label>
                  <input
                    type="number" min={0} max={50000000} step={10000}
                    value={form.funding_usd}
                    onChange={(e) => setForm({ ...form, funding_usd: Number(e.target.value) })}
                    className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                    data-testid="founders-form-funding"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">Website (optional)</label>
                  <input
                    type="url"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="https://"
                    className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                    data-testid="founders-form-website"
                  />
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2">Pitch (full description)</label>
                <textarea
                  rows={6}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Problem · solution · traction · why now · what you need."
                  className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-4 py-3 text-white"
                  data-testid="founders-form-description"
                />
              </div>

              {/* Traction signals — power the multi-dimensional founder score */}
              <div className="border border-dashed border-white/15 p-4 bg-black/20">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF9500] mb-3">
                  / Traction signals · powers your founder score (0-100)
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-white/55 mb-1">Team size</label>
                    <input type="number" min={1} max={50} value={form.team_size}
                      onChange={(e) => setForm({ ...form, team_size: Math.max(1, Number(e.target.value) || 1) })}
                      className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-3 py-2 text-white text-sm"
                      data-testid="founders-form-team-size" />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-white/55 mb-1">Monthly users</label>
                    <input type="number" min={0} value={form.monthly_users}
                      onChange={(e) => setForm({ ...form, monthly_users: Math.max(0, Number(e.target.value) || 0) })}
                      className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-3 py-2 text-white text-sm"
                      data-testid="founders-form-monthly-users" />
                  </div>
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-white/55 mb-1">Months of traction</label>
                    <input type="number" min={0} max={120} value={form.months_traction}
                      onChange={(e) => setForm({ ...form, months_traction: Math.max(0, Number(e.target.value) || 0) })}
                      className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-3 py-2 text-white text-sm"
                      data-testid="founders-form-months-traction" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block font-mono text-[10px] uppercase tracking-widest text-white/55 mb-1">Prior exits (team)</label>
                    <input type="number" min={0} max={20} value={form.prior_exits}
                      onChange={(e) => setForm({ ...form, prior_exits: Math.max(0, Number(e.target.value) || 0) })}
                      className="w-full bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-3 py-2 text-white text-sm"
                      data-testid="founders-form-prior-exits" />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer mt-6">
                    <input type="checkbox" checked={form.has_revenue}
                      onChange={(e) => setForm({ ...form, has_revenue: e.target.checked })}
                      className="w-4 h-4 accent-[#FF9500]"
                      data-testid="founders-form-has-revenue" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/70">Already generating revenue</span>
                  </label>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 bg-[#FF9500] text-black hover:bg-[#FF9500]/85 px-6 py-3 font-bold transition-colors disabled:opacity-50"
                  data-testid="founders-form-submit"
                >
                  {submitting ? "Submitting…" : "Publish to investors"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-white/15 hover:border-white font-mono text-[10px] uppercase tracking-widest"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SCORE BREAKDOWN — shown after successful submit so the founder
          immediately sees how the matching engine graded their pitch. */}
      {justScored && justScored.score && (
        <section id="founder-score-card" className="border-t border-white/10 py-16 bg-gradient-to-br from-[#1a1310] via-[#0c0c0d] to-[#0c0c0d]">
          <div className="max-w-4xl mx-auto px-6 lg:px-12" data-testid="founder-score-card">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF9500] mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> / Your founder score
            </div>
            <div className="flex flex-col md:flex-row md:items-end gap-6 mb-8">
              <div>
                <div className="font-display font-black text-6xl md:text-7xl text-[#FF9500] leading-none" data-testid="founder-score-overall">
                  {justScored.score.overall}<span className="text-3xl text-white/40">/100</span>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/55 mt-2">
                  {justScored.title} · graded by {justScored.score.engine || "engine"}
                </div>
              </div>
              {justScored.score.rationale && (
                <p className="flex-1 text-sm md:text-base text-white/75 italic leading-relaxed border-l-2 border-[#FF9500]/60 pl-4">
                  "{justScored.score.rationale}"
                </p>
              )}
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: "idea",        label: "Idea" },
                { key: "team",        label: "Team" },
                { key: "market",      label: "Market" },
                { key: "execution",   label: "Execution" },
                { key: "traction",    label: "Traction" },
                { key: "scalability", label: "Scalability" },
              ].map((d) => {
                const v = Math.max(0, Math.min(100, Number(justScored.score[d.key]) || 0));
                return (
                  <div key={d.key} className="bg-[#141416] border border-white/10 p-4" data-testid={`founder-score-${d.key}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-white/55">{d.label}</span>
                      <span className="font-display font-bold text-2xl text-white">{v}</span>
                    </div>
                    <div className="h-1.5 bg-white/10 overflow-hidden">
                      <div
                        className="h-full bg-[#FF9500]"
                        style={{ width: `${v}%`, transition: "width 600ms ease-out" }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-6 text-xs text-white/55">
              This score helps investors rank your project against their thesis. A higher score
              = more gold-tier placements in matched investor feeds. You can re-submit anytime
              with stronger traction signals.
            </p>
          </div>
        </section>
      )}

      {/* MY PROJECTS */}
      {user && myProjects.length > 0 && (
        <section className="border-t border-white/10 py-16 bg-[#0c0c0d]">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/55 mb-3">/ MY PROJECTS</div>
            <h2 className="font-display font-black text-3xl tracking-tighter mb-8">
              {myProjects.length} live in the investor feed
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {myProjects.map((p) => (
                <div key={p.id} className="bg-[#141416] border border-white/10 p-5" data-testid={`founder-project-${p.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF9500]">{p.category}</span>
                    <button onClick={() => deleteProject(p.id)} className="text-white/40 hover:text-white text-xs">Remove</button>
                  </div>
                  <div className="font-display font-bold text-lg mb-1">{p.title}</div>
                  <p className="text-sm text-white/70 mb-3">{p.one_liner}</p>
                  <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-white/45">
                    <span>{p.stage}</span>
                    <span>·</span>
                    <span>${p.funding_usd?.toLocaleString() || 0} target</span>
                    <span>·</span>
                    <span className="text-[#FF9500]">{p.interest_count || 0} interested</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY */}
      <section className="border-t border-white/10 py-20 bg-[#0c0c0d]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/55 mb-3">/ WHY MAQAM FOR FOUNDERS</div>
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-10">
            We've already done the cold outreach.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Briefcase, t: "Pre-built investor network", d: "Hundreds of KSA and international VCs, angels, and family offices already in the system." },
              { icon: Target,    t: "Category-matched",           d: "Your project surfaces in gold for investors who fund your sector — never wasted intros." },
              { icon: Users,     t: "Founder-first",              d: "No fee on the founder side. Investors pay to access the feed. You just submit and wait." },
              { icon: Building2, t: "KSA-deep",                   d: "Vision 2030 alignment matters. We surface KSA-focused projects to the funds writing those checks." },
              { icon: Rocket,    t: "Two-tap intros",             d: "Investor signals interest → your contact is unlocked for them. No more cold-pitching." },
              { icon: Sparkles,  t: "Stay updated",               d: "Edit your project anytime. Higher traction = climbs the feed." },
            ].map((f) => (
              <div key={f.t} className="border-l-2 border-[#FF9500] pl-5">
                <f.icon className="w-5 h-5 text-[#FF9500] mb-3" />
                <div className="font-display font-bold text-lg mb-1">{f.t}</div>
                <p className="text-sm text-white/65 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Investor link */}
      <section className="border-t border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-6">
            Your dream is one form away from <span className="text-[#FF9500]">the right investor</span>.
          </h2>
          <button
            type="button"
            onClick={() => { if (!user) navigate("/signup?next=/founders"); else setShowForm(true); }}
            className="inline-flex items-center justify-center gap-3 bg-[#FF9500] text-black hover:bg-[#FF9500]/85 px-10 py-5 font-bold text-base transition-colors mt-6"
            data-testid="founders-cta-bottom"
          >
            Submit your project <ArrowRight className="w-5 h-5" />
          </button>
          <div className="mt-12 pt-12 border-t border-white/10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-3">
              Are you an investor?
            </div>
            <Link to="/investors" className="text-[#FF9500] hover:text-white transition-colors font-bold" data-testid="founders-to-investors">
              See the investor feed → /investors
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
